import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartValueAxis, ChartValueAxisItem, ChartTooltip } from '@progress/kendo-react-charts';
import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { State as GridState, AggregateDescriptor, process } from '@progress/kendo-data-query';
import { fetchApi } from 'services/api';
import { Title } from 'utils/title';
import useCursorLoading from 'utils/useCursorLoading';
import SimpleDateRangePicker from 'components/SimpleDateRangePicker';
import PopupNavBar from 'components/Layout/PopupNavBar';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { setGroupIds } from '@progress/kendo-react-data-tools';
import { formatNumber } from '@progress/kendo-intl';
import { Link } from 'react-router-dom';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';

export type UtilizationItem = {
  Date: string;
  EquipmentType: string;
  Percentage: number;
};

export type ServiceStatusItem = {
  Date: string;
  EquipmentType: string;
  InService: number;
  OutService: number;
};

export type TurndownItem = {
  Date: string;
  EquipmentType: string;
  Turndown: number;
  Total: number;
};

export type UnseatedItem = {
  Date: string;
  EquipmentType: string;
  Unseated: number;
  Total: number;
};

export type AssetBrokerageSummaryItem = {
  Date: string;
  AssetType: string;
  LoadsHauled: number;
  BillableMiles: number;
  Revenue: number;
  RevenueExcludingFuel: number;
};

export type PayByVehicleItem = {
  Date: string;
  VehicleID: number;
  VehicleNumber: string;
  VehicleTypeID: number;
  VehicleType: string;
  OwnerID: number | null;
  OwnerName: string;
  TotalPay: number;
};

export type CompletedTripsItem = {
  Date: string;
  EquipmentType: string;
  CompletedTrips: number;
};

const initialDataState: GridState = {
  group: [{ field: 'VehicleType' }]
};

const aggregates: AggregateDescriptor[] = [
  { field: 'VehicleNumber', aggregate: 'count' },
  { field: 'TotalPay', aggregate: 'average' }
];

const processWithGroups = (data: PayByVehicleItem[], dataState: GridState) => {
  if (dataState.group) {
    dataState.group.forEach((group) => (group.aggregates = aggregates));
  }

  const newDataState = process(data, dataState);
  setGroupIds({ data: newDataState.data, group: dataState.group });

  return newDataState;
};

const vehicleTypes = [
  { ID: 1, Name: 'All Vehicles' },
  { ID: 2, Name: 'Owner Operators' },
  { ID: 3, Name: 'Company Vehicles' }
];

const baseAssetTypes = ['Assets-Company', 'Assets-O/O', 'Brokerage'] as const;

type AssetMetricField = 'Revenue' | 'BillableMiles' | 'LoadsHauled' | 'RevenueExcludingFuel';

type AssetMetricOption = {
  field: AssetMetricField;
  label: string;
  axisFormat: string;
  tooltipFormat: string;
};

const assetMetricOptions: AssetMetricOption[] = [
  { field: 'Revenue', label: 'Revenue', axisFormat: '{0:c0}', tooltipFormat: '{0:c0}' },
  { field: 'RevenueExcludingFuel', label: 'Revenue w/o Fuel', axisFormat: '{0:c0}', tooltipFormat: '{0:c0}' },
  { field: 'BillableMiles', label: 'Billable Miles', axisFormat: '{0:n0}', tooltipFormat: '{0:n0}' },
  { field: 'LoadsHauled', label: 'Loads Hauled', axisFormat: '{0:n0}', tooltipFormat: '{0:n0}' }
];

type RenderSeriesOptions = {
  showLegend?: boolean;
};

const FleetDashboard = () => {
  const { status } = useThemeSwitcher();
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(dayjs().tz('America/New_York').startOf('isoWeek').subtract(1, 'week').toDate());
  const [toDate, setToDate] = useState(dayjs().tz('America/New_York').endOf('isoWeek').subtract(1, 'week').startOf('day').toDate());
  const [includeCompany, setIncludeCompany] = useState(true);
  const [includeOwnerOperators, setIncludeOwnerOperators] = useState(true);
  const [includeWeekdays, setIncludeWeekdays] = useState(true);
  const [includeWeekends, setIncludeWeekends] = useState(true);
  const [utilization, setUtilization] = useState<UtilizationItem[]>([]);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatusItem[]>([]);
  const [turndowns, setTurndowns] = useState<TurndownItem[]>([]);
  const [unseated, setUnseated] = useState<UnseatedItem[]>([]);
  const [revenue, setRevenue] = useState<PayByVehicleItem[]>([]);
  const [completedTrips, setCompletedTrips] = useState<CompletedTripsItem[]>([]);
  const [assetSummary, setAssetSummary] = useState<AssetBrokerageSummaryItem[]>([]);
  const [revenueState, setRevenueState] = React.useState<GridState>(initialDataState);
  const [revenueResultState, setRevenueResultState] = React.useState(processWithGroups(revenue, initialDataState));
  const [showUnseatedPercent, setShowUnseatedPercent] = useState(false);
  const [showTurndownPercent, setShowTurndownPercent] = useState(false);
  const [summarizeByWeek, setSummarizeByWeek] = useState(false);
  const [includePeriodComparison, setIncludePeriodComparison] = useState(false);
  const [includeAllEquipmentType, setIncludeAllEquipmentType] = useState(false);
  const [utilizationVisible, setUtilizationVisible] = useState<{ [key: string]: boolean }>({});
  const [assetMetric, setAssetMetric] = useState<AssetMetricOption>(assetMetricOptions[0]);

  const showPeriodComparison = summarizeByWeek && includePeriodComparison;
  const dateStrings = useMemo(() => [...new Set(utilization.map(u => u.Date))].sort(), [utilization]);
  const dates = useMemo(() => dateStrings.map(d => dayjs(d, 'YYYY-MM-DD').toDate()), [dateStrings]);
  const chartDates = useMemo(
    () => (showPeriodComparison && dates.length > 2 ? dates.slice(Math.floor(dates.length / 2)) : dates),
    [dates, showPeriodComparison]
  );
  const equipmentTypes = useMemo(() => [...new Set(utilization.map(u => u.EquipmentType))], [utilization]);

  useEffect(() => {
    setUtilizationVisible(prev => {
      const newState: { [key: string]: boolean } = {};
      equipmentTypes.forEach(type => {
        newState[type] = prev[type] !== undefined ? prev[type] : true;
      });
      return newState;
    });
  }, [equipmentTypes]);

  useCursorLoading(loading);

  const handleDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setRevenueState(event.dataState);
  }, []);

  useEffect(() => {
    setRevenueResultState(processWithGroups(revenue, revenueState));
  }, [revenue, revenueState]);

  const getSeriesData = useCallback(
    <T extends { EquipmentType: string; Date: string }>(items: T[], value: (item: T | undefined) => number | null) =>
      equipmentTypes.map((type, index) => ({
        name: type,
        color: `var(--kendo-color-series-${String.fromCharCode(97 + index)})`,
        data: dateStrings.map(ds => value(items.find(i => i.EquipmentType === type && i.Date === ds)))
      })),
    [equipmentTypes, dateStrings]
  );

  const utilizationSeries = useMemo(() =>
    getSeriesData(utilization, i => (i ? i.Percentage : null)),
    [getSeriesData, utilization]
  );

  const serviceSeries = useMemo(() =>
    getSeriesData(serviceStatus, i => (i ? i.InService / (i.InService + i.OutService) : null)),
    [getSeriesData, serviceStatus]
  );

  const turndownSeries = useMemo(() =>
    getSeriesData(turndowns, i => {
      if (!i) return null;
      return showTurndownPercent ? i.Turndown / i.Total : i.Turndown;
    }),
    [getSeriesData, turndowns, showTurndownPercent]
  );

  const unseatedSeries = useMemo(() =>
    getSeriesData(unseated, i => {
      if (!i) return null;
      return showUnseatedPercent ? i.Unseated / i.Total : i.Unseated;
    }),
    [getSeriesData, unseated, showUnseatedPercent]
  );

  const completedTripsSeries = useMemo(() =>
    getSeriesData(completedTrips, i => (i ? i.CompletedTrips : null)),
    [getSeriesData, completedTrips]
  );

  const assetSummaryIndex = useMemo(() => {
    return assetSummary.reduce<Record<string, Record<string, AssetBrokerageSummaryItem>>>((acc, item) => {
      if (!acc[item.Date]) acc[item.Date] = {};
      acc[item.Date][item.AssetType] = item;
      return acc;
    }, {});
  }, [assetSummary]);

  const assetBrokerageSeries = useMemo(() => {
    const metricField = assetMetric.field;
    const types = ['Total', ...baseAssetTypes];
    return types.map((type) => ({
      name: type,
      color: type === 'Brokerage' ? 'var(--kendo-color-series-d)'
        : type === 'Assets-O/O' ? 'var(--kendo-color-series-c)'
        : type === 'Assets-Company' ? 'var(--kendo-color-series-a)'
        : type === 'Total' ? 'var(--kendo-color-series-b)'
        : 'var(--kendo-color-series-e)',
      data: dateStrings.map(ds => {
        if (type === 'Total') {
          return baseAssetTypes.reduce((sum, assetType) => sum + (assetSummaryIndex[ds]?.[assetType]?.[metricField] ?? 0), 0);
        }
        return assetSummaryIndex[ds]?.[type]?.[metricField] ?? 0;
      })
    }));
  }, [assetMetric, assetSummaryIndex, dateStrings]);

  const renderSeries = useCallback(
    (series: { name: string; data: (number | null)[]; color: string }[], options: RenderSeriesOptions = {}) =>
      showPeriodComparison && dates.length > 2
        ? series.flatMap(s => {
            const half = Math.floor(s.data.length / 2);
            return [
              <ChartSeriesItem
                key={`${s.name}-dashed`}
                type="line"
                name={s.name}
                data={s.data.slice(0, half)}
                color={s.color}
                dashType="dash"
                visible={utilizationVisible[s.name] !== false}
                visibleInLegend={false}
              />,
              <ChartSeriesItem
                key={`${s.name}-solid`}
                type="line"
                name={s.name}
                data={s.data.slice(half)}
                color={s.color}
                visible={utilizationVisible[s.name] !== false}
                visibleInLegend={options.showLegend ?? false}
              />
            ];
          })
        : series.map(s => (
            <ChartSeriesItem
              key={s.name}
              type="line"
              name={s.name}
              data={s.data}
              color={s.color}
              visible={utilizationVisible[s.name] !== false}
              visibleInLegend={options.showLegend ?? false}
            />
          )),
    [showPeriodComparison, utilizationVisible, dates.length]
  );

  const refresh = useCallback(async () => {
    if (!fromDate || !toDate) return;
    if (!includeCompany && !includeOwnerOperators) return;
    if (!includeWeekdays && !includeWeekends) return;

    setLoading(true);
    const data = {
      FromDate: fromDate,
      ToDate: toDate,
      IncludeCompany: includeCompany,
      IncludeOwnerOperators: includeOwnerOperators,
      IncludeWeekdays: includeWeekdays,
      IncludeWeekends: includeWeekends,
      SummarizeByWeek: summarizeByWeek,
      IncludePeriodComparison: showPeriodComparison,
      IncludeAllEquipmentType: includeAllEquipmentType
    };

    try {
      const utilizationPromise = fetchApi('/api/FleetDashboard/Utilization', data, 'POST')
        .then(result => setUtilization(result))
        .catch(err => {
          setUtilization([]);
          console.error('Error fetching Utilization:', err)
        });

      const serviceStatusPromise = fetchApi('/api/FleetDashboard/ServiceStatus', data, 'POST')
        .then(result => setServiceStatus(result))
        .catch(err => {
          setServiceStatus([]);
          console.error('Error fetching Service Status:', err)
        });

      const turndownsPromise = fetchApi('/api/FleetDashboard/Turndowns', data, 'POST')
        .then(result => setTurndowns(result))
        .catch(err => {
          setTurndowns([]);
          console.error('Error fetching Turndowns:', err)
        });

      const unseatedPromise = fetchApi('/api/FleetDashboard/Unseated', data, 'POST')
        .then(result => setUnseated(result))
        .catch(err => {
          setUnseated([]);
          console.error('Error fetching Unseated:', err)
        });

      const revenuePromise = fetchApi('/api/FleetDashboard/PayByVehicle', data, 'POST')
        .then(result => setRevenue(result))
        .catch(err => {
          setRevenue([]);
          console.error('Error fetching Revenue:', err)
        });

      const completedTripsPromise = fetchApi('/api/FleetDashboard/CompletedTrips', data, 'POST')
        .then(result => setCompletedTrips(result))
        .catch(err => {
          setCompletedTrips([]);
          console.error('Error fetching Completed Trips:', err)
        });

      const assetSummaryPromise = fetchApi('/api/FleetDashboard/AssetBrokerageSummary', data, 'POST')
        .then(result => setAssetSummary(result))
        .catch(err => {
          setAssetSummary([]);
          console.error('Error fetching Asset/Brokerage Summary:', err)
        });

      // Wait for all promises to settle, but state updates happen as each resolves
      await Promise.allSettled([
        utilizationPromise,
        serviceStatusPromise,
        turndownsPromise,
        unseatedPromise,
        revenuePromise,
        completedTripsPromise,
        assetSummaryPromise,
      ]);
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      alert('Error fetching dashboard');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, includeCompany, includeOwnerOperators, includeWeekdays, includeWeekends, summarizeByWeek, showPeriodComparison, includeAllEquipmentType]);

  useEffect(() => { refresh(); }, [refresh]);

  if (status !== 'loaded') return <div>Loading...</div>;

  const RevenueFooterCustomCell = (props: GridCustomCellProps) => {
    let cellContent;
    if (props.field === 'TotalPay') {
      cellContent = `Avg: ${formatNumber(props.dataItem.aggregates.TotalPay.average, 'c2')}`;
    } else if (props.field === 'VehicleNumber') {
      cellContent = `${formatNumber(props.dataItem.aggregates.VehicleNumber.count, 'n0')} Unit${props.dataItem.aggregates.VehicleNumber.count > 1 ? 's' : ''}`;
    }

    return (
      <td {...props.tdProps} colSpan={1}>
        {cellContent}
      </td>
    );
  };

  const chartBaseUnit = summarizeByWeek ? 'weeks' : 'days';
  return (
    <React.Fragment>
      <PopupNavBar />
      <Title string="Fleet Dashboard" />
      <nav className="navbar navbar-dark bg-dark">
        <a className="navbar-brand" href="/">Fleet Dashboard {loading && <Loader type="pulsing" className="p-0" />}</a>
        <ul className="nav navbar-nav navbar-right">
          <Button
            title="Refresh"
            icon="refresh"
            svgIcon={arrowRotateCwIcon}
            disabled={loading}
            onClick={refresh}
          />
        </ul>
      </nav>
      <div className="container-fluid">
        <div className="row pt-2 mb-3">
          <div className="col-md-4">
            <SimpleDateRangePicker
              inputProps={{ disabled: loading }}
              value={{ start: fromDate, end: toDate }}
              onChange={(e) => {
                if (summarizeByWeek)
                {
                  // Adjust to start of isoWeek
                  setFromDate(dayjs(e.start).tz('America/New_York').startOf('isoWeek').toDate());
                } else {
                  setFromDate(e.start);
                }
                setToDate(e.end);
              }}
            />
          </div>
          <div className="col-md-2">
            <DropDownList
              data={vehicleTypes}
              textField="Name"
              dataItemKey="ID"
              value={vehicleTypes.find(v => (includeCompany && includeOwnerOperators && v.ID === 1) || (includeOwnerOperators && !includeCompany && v.ID === 2) || (includeCompany && !includeOwnerOperators && v.ID === 3))}
              onChange={(e) => {
                const value = e.target.value.ID;
                setIncludeOwnerOperators(value === 1 || value === 2);
                setIncludeCompany(value === 1 || value === 3);
              }}
            />
          </div>
          <div className="col-md-2">
            <ButtonGroup>
              <Button
                togglable
                selected={includeWeekdays}
                onClick={() => {
                  setIncludeWeekdays(!includeWeekdays);
                  setSummarizeByWeek(false);
                }}
              >
                Weekdays
              </Button>
              <Button
                togglable
                selected={includeWeekends}
                onClick={() => {
                  setIncludeWeekends(!includeWeekends);
                  setSummarizeByWeek(false);
                }}
              >
                Weekends
              </Button>
            </ButtonGroup>
          </div>
          <div className="col-md-2 d-flex align-items-center">
            <Checkbox
              label="Summarize by Week"
              checked={summarizeByWeek}
              onChange={e => {
                setSummarizeByWeek(e.value);
                setIncludeWeekdays(true);
                setIncludeWeekends(true);
                if (e.value)
                {
                  // Adjust to start of isoWeek
                  setFromDate(dayjs(fromDate).tz('America/New_York').startOf('isoWeek').toDate());
                }
              }}
              disabled={loading}
            />
          </div>
          {summarizeByWeek && <div className="col-md-2 d-flex align-items-center">
            <Checkbox
              className="mt-2"
              label="Show Period Comparison"
              checked={includePeriodComparison}
              onChange={e => setIncludePeriodComparison(e.value)}
              disabled={!summarizeByWeek || loading}
            />
          </div>}
          <div className="col-md-2 d-flex align-items-center">
            <Checkbox
              className="mt-2"
              label="Group Equipment Types"
              checked={includeAllEquipmentType}
              onChange={e => setIncludeAllEquipmentType(e.value)}
              disabled={loading}
            />
          </div>
        </div>
        {!includeAllEquipmentType && <div className="row mb-2">
          <div className="col-12 d-flex align-items-center">
            <ButtonGroup>
              {equipmentTypes.map(type => (
                <Button
                  key={type}
                  togglable
                  selected={utilizationVisible[type] !== false}
                  onClick={() =>
                    setUtilizationVisible(v => ({ ...v, [type]: !v[type] }))
                  }
                  style={{
                    fontWeight: 'bold',
                    borderBottom: `3px solid ${utilizationSeries.find(s => s.name === type)?.color || 'black'}`,
                    color: utilizationVisible[type] !== false ? 'black' : '#ccc',
                  }}
                >
                  {type}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </div>}
        <div className="row">
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Utilization</h5>
            </div>
            <Chart transitions={false}>
              <ChartLegend visible={false} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem min={0} max={1} labels={{ format: '{0:p0}' }} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(utilizationSeries)}
                <ChartSeriesItem type="line" data={chartDates.map(() => 0.75)} name="Target" color="red" />
              </ChartSeries>
              <ChartTooltip format="{0:p0}" />
            </Chart>
          </div>
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">In Service</h5>
            </div>
            <Chart transitions={false}>
              <ChartLegend visible={false} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem labels={{ format: '{0:p0}' }} max={1} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(serviceSeries)}
                <ChartSeriesItem type="line" data={chartDates.map(() => 0.85)} name="Target" color="red" />
              </ChartSeries>
              <ChartTooltip format="{0:p0}" />
            </Chart>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Turndowns</h5>
              <ButtonGroup>
                <Button togglable onClick={() => setShowTurndownPercent(false)} selected={!showTurndownPercent}>Count</Button>
                <Button togglable onClick={() => setShowTurndownPercent(true)} selected={showTurndownPercent}>%</Button>
              </ButtonGroup>
            </div>
            <Chart transitions={false} style={{ height: 300 }}>
              <ChartLegend visible={false} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem labels={{ format: showTurndownPercent ? '{0:p0}' : '{0:n0}' }} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(turndownSeries)}
                {showTurndownPercent ? <ChartSeriesItem type="line" data={chartDates.map(() => 0.1)} name="Target" color="red" /> : null}
              </ChartSeries>
              <ChartTooltip format={showTurndownPercent ? '{0:p2}' : '{0:n0}'} />
            </Chart>
          </div>
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Unseated Units</h5>
              <ButtonGroup>
                <Button togglable onClick={() => setShowUnseatedPercent(false)} selected={!showUnseatedPercent}>Count</Button>
                <Button togglable onClick={() => setShowUnseatedPercent(true)} selected={showUnseatedPercent}>%</Button>
              </ButtonGroup>
            </div>
            <Chart transitions={false} style={{ height: 300 }}>
              <ChartLegend visible={false} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem labels={{ format: showUnseatedPercent ? '{0:p0}' : '{0:n0}' }} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(unseatedSeries)}
                {showUnseatedPercent ? <ChartSeriesItem type="line" data={chartDates.map(() => 0.15)} name="Target" color="red" /> : null}
              </ChartSeries>
              <ChartTooltip format={showUnseatedPercent ? '{0:p2}' : '{0:n0}'} />
            </Chart>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Completed Trips</h5>
            </div>
            <Chart transitions={false}>
              <ChartLegend visible={false} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem labels={{ format: '{0:n0}' }} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(completedTripsSeries)}
              </ChartSeries>
              <ChartTooltip format="{0:n0}" />
            </Chart>
          </div>
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <h5 className="mb-0">Asset & Brokerage Summary</h5>
              <DropDownList
                style={{ width: 180 }}
                data={assetMetricOptions}
                textField="label"
                dataItemKey="field"
                value={assetMetric}
                onChange={e => setAssetMetric(e.target.value as AssetMetricOption)}
                disabled={loading}
              />
            </div>
            <Chart transitions={false}>
              <ChartLegend position="bottom" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem type="date" baseUnit={chartBaseUnit} categories={chartDates} weekStartDay={1} />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem labels={{ format: assetMetric.axisFormat }} />
              </ChartValueAxis>
              <ChartSeries>
                {renderSeries(assetBrokerageSeries, { showLegend: true })}
              </ChartSeries>
              <ChartTooltip format={assetMetric.tooltipFormat} />
            </Chart>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-12">
            <Grid
              sortable
              groupable={{ footer: 'always', enabled: true }}
              onDataStateChange={handleDataStateChange}
              data={revenueResultState.data}
              total={revenueResultState.total}
              {...revenueState}
              cells={{
                groupFooter: RevenueFooterCustomCell
              }}
            >
              <Column field="VehicleNumber" title="Unit #" cells={{ data: (props) => {
                return <td {...props.tdProps}>
                  <Link
                    style={{ color: '#007bff' }}
                    to={`/Assets/Vehicle/${props.dataItem.VehicleID}`}
                  >
                    {props.dataItem.VehicleNumber}
                  </Link>
                </td>
              }}} />
              <Column field="VehicleType" title="Vehicle Type" />
              <Column field="OwnerName" title="Owner" cells={{ data: (props) => {
                return <td {...props.tdProps}>
                  <Link
                    style={{ color: '#007bff' }}
                    to={`/Assets/Owner/${props.dataItem.OwnerID}`}
                  >
                    {props.dataItem.OwnerName}
                  </Link>
                </td>
              }}} />
              <Column field="TotalPay" title="Total Pay" format="{0:c}" />
            </Grid>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default FleetDashboard;
